import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/useToast";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { Add, Edit, Delete, People } from "@mui/icons-material";
import { validateRequiredName, validatePositiveInt } from "@/lib/formValidation";
import { unwrapPagedList } from "@/lib/bookingApi";
import { useAdminServicesCatalog } from "@/hooks/useAdminPagedCatalogs";
import { dialogActionsSx, dialogContentOutlinedFormSx, dialogPaperPropsLabelSafe } from "@/lib/dialogStyles";
import type { Resource, Service } from "@shared/schema";

const RESOURCE_TYPE_VALUES = ["table", "room", "bay", "lane", "court", "tee_time", "other"] as const;

const TYPE_COLORS: Record<string, "default" | "primary" | "secondary" | "success" | "warning" | "info"> = {
  table: "success",
  room: "primary",
  bay: "warning",
  tee_time: "success",
  other: "default",
};

interface ResourceFormState {
  name: string;
  description: string;
  type: string;
  capacity: number;
  active: boolean;
  /** UI stores string ids (aligned with `Service.id`). */
  serviceIds: string[];
}

const defaultForm: ResourceFormState = {
  name: "",
  description: "",
  type: "other",
  capacity: 1,
  active: true,
  serviceIds: [],
};

/** Integer ids when numeric-only; otherwise preserve string (e.g. mock `svc-1`). */
function resourceServiceIdsForRequest(ids: string[]): Array<number | string> {
  return ids.map((id) => {
    const t = id.trim();
    if (/^-?\d+$/.test(t)) {
      const n = Number(t);
      if (Number.isSafeInteger(n)) return n;
    }
    return id;
  });
}

export default function AdminResources() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { services: servicesCatalog, isLoading: servicesCatalogLoading } = useAdminServicesCatalog();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Resource | null>(null);
  const [form, setForm] = useState<ResourceFormState>(defaultForm);
  const [resourceFieldErrors, setResourceFieldErrors] = useState<{
    name?: string;
    capacity?: string;
    serviceIds?: string;
  }>({});
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);

  const { data: resources, isLoading: resourcesLoading } = useQuery<Resource[]>({
    queryKey: ["/api/admin/resources"],
    select: (raw: unknown) => {
      const rows = unwrapPagedList<Resource & { businessId?: string }>(raw);
      return rows.map((r) => {
        const raw = r as Resource & { serviceIds?: unknown };
        const sid = raw.serviceIds;
        const serviceIds = Array.isArray(sid) ? sid.map((x) => String(x)) : null;
        return {
          id: String(r.id),
          name: r.name,
          description: r.description ?? null,
          type: r.type ?? "other",
          capacity: typeof r.capacity === "number" ? r.capacity : 1,
          active: r.active ?? true,
          serviceIds,
        };
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: ResourceFormState) =>
      apiRequest("POST", "/api/admin/resources", {
        name: data.name,
        description: data.description || undefined,
        type: data.type,
        capacity: data.capacity,
        active: data.active,
        serviceIds: resourceServiceIdsForRequest(data.serviceIds),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/resources"] });
      toast({ title: t("toasts.resourceCreated") });
      setDialogOpen(false);
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ResourceFormState }) =>
      apiRequest("PATCH", `/api/admin/resources/${id}`, {
        name: data.name,
        description: data.description || undefined,
        type: data.type,
        capacity: data.capacity,
        active: data.active,
        serviceIds: resourceServiceIdsForRequest(data.serviceIds),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/resources"] });
      toast({ title: t("toasts.resourceUpdated") });
      setDialogOpen(false);
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/resources/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/resources"] });
      toast({ title: t("toasts.resourceDeleted") });
      setDeleteTarget(null);
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  function openCreate() {
    setEditing(null);
    setForm(defaultForm);
    setResourceFieldErrors({});
    setDialogOpen(true);
  }

  function openEdit(resource: Resource) {
    setEditing(resource);
    setForm({
      name: resource.name,
      description: resource.description || "",
      type: resource.type,
      capacity: resource.capacity,
      active: resource.active ?? true,
      serviceIds: resource.serviceIds?.length ? [...resource.serviceIds] : [],
    });
    setResourceFieldErrors({});
    setDialogOpen(true);
  }

  function handleSubmit() {
    const next: { name?: string; capacity?: string; serviceIds?: string } = {};
    const nameErr = validateRequiredName(form.name, t("adminResources.name"));
    if (nameErr) next.name = nameErr;
    const capErr = validatePositiveInt(form.capacity, t("adminResources.capacity"), 1);
    if (capErr) next.capacity = capErr;
    if (form.serviceIds.length === 0) {
      next.serviceIds = t("adminResources.linkedServicesRequired");
    }
    setResourceFieldErrors(next);
    if (Object.keys(next).length > 0) return;
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  }

  const serviceNameById = useMemo(() => {
    const m = new Map<string, string>();
    servicesCatalog.forEach((s) => m.set(String(s.id), s.name));
    return m;
  }, [servicesCatalog]);

  const selectableServices = useMemo(
    () => servicesCatalog.filter((s: Service) => s.active !== false).sort((a, b) => a.name.localeCompare(b.name)),
    [servicesCatalog],
  );

  /** Active offerings for picking; inactive ones already linked are merged in so edits don’t drop them. */
  const linkedServicesOptions = useMemo(() => {
    const byId = new Map<string, Service>(servicesCatalog.map((s) => [String(s.id), s]));
    const base = [...selectableServices];
    const seen = new Set(base.map((s) => String(s.id)));
    for (const id of form.serviceIds) {
      const s = byId.get(id);
      if (s && !seen.has(id)) {
        base.push(s);
        seen.add(id);
      }
    }
    base.sort((a, b) => a.name.localeCompare(b.name));
    return base;
  }, [servicesCatalog, selectableServices, form.serviceIds]);

  const linkedServicesValue = useMemo(() => {
    const byId = new Map(servicesCatalog.map((s) => [String(s.id), s]));
    return form.serviceIds.map((id) => byId.get(id)).filter((s): s is Service => s != null);
  }, [servicesCatalog, form.serviceIds]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  /** Cards resolve offering names from the full catalog; wait for all service pages, not only resources. */
  const pageLoading = resourcesLoading || servicesCatalogLoading;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t("adminResources.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("adminResources.subtitle")}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate} data-testid="button-add-resource">
          {t("adminResources.addResource")}
        </Button>
      </Box>

      {pageLoading ? (
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" } }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={160} />
          ))}
        </Box>
      ) : !resources?.length ? (
        <Card variant="outlined">
          <CardContent sx={{ py: 8, textAlign: "center" }}>
            <People sx={{ fontSize: 40, color: "action.disabled", mb: 2 }} />
            <Typography fontWeight={600}>{t("adminResources.emptyTitle")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("adminResources.emptyBody")}
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              {t("adminResources.addFirst")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" } }}>
          {resources.map((resource) => (
            <Card key={resource.id} variant="outlined" sx={{ opacity: resource.active ? 1 : 0.65 }} data-testid={`card-resource-${resource.id}`}>
              <CardHeader
                title={resource.name}
                titleTypographyProps={{ variant: "subtitle1" }}
                action={
                  <Box>
                    <IconButton size="small" onClick={() => openEdit(resource)} data-testid={`button-edit-resource-${resource.id}`}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(resource)} data-testid={`button-delete-resource-${resource.id}`}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                }
              />
              <CardContent sx={{ pt: 0 }}>
                {resource.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {resource.description}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={t(`adminResources.types.${resource.type}`, { defaultValue: resource.type })}
                    color={TYPE_COLORS[resource.type] ?? "default"}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <People sx={{ fontSize: 14 }} />
                    {t("adminResources.capacityLabel", { count: resource.capacity })}
                  </Typography>
                  {!resource.active && <Chip size="small" label={t("adminResources.inactive")} />}
                </Box>
                {resource.serviceIds && resource.serviceIds.length > 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {resource.serviceIds.map((id) => serviceNameById.get(id) ?? id).join(", ")}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={dialogPaperPropsLabelSafe}
      >
        <DialogTitle>{editing ? t("adminResources.editResource") : t("adminResources.newResource")}</DialogTitle>
        <DialogContent sx={dialogContentOutlinedFormSx}>
          <TextField
            required
            label={t("adminResources.name")}
            value={form.name}
            onChange={(e) => {
              setForm({ ...form, name: e.target.value });
              if (resourceFieldErrors.name) setResourceFieldErrors((prev) => ({ ...prev, name: undefined }));
            }}
            error={Boolean(resourceFieldErrors.name)}
            helperText={resourceFieldErrors.name}
            fullWidth
            data-testid="input-resource-name"
          />
          <TextField
            label={t("adminResources.description")}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            data-testid="input-resource-description"
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField select label={t("adminResources.type")} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="select-resource-type">
              {RESOURCE_TYPE_VALUES.map((v) => (
                <MenuItem key={v} value={v}>
                  {t(`adminResources.types.${v}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t("adminResources.capacity")}
              type="number"
              inputProps={{ min: 1 }}
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value, 10) || 1 })}
              data-testid="input-resource-capacity"
            />
          </Box>
          {selectableServices.length === 0 ? (
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                {t("adminResources.linkedServices")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("adminResources.linkedServicesNoCatalog")}
              </Typography>
            </Box>
          ) : (
            <Autocomplete<Service, true, false, false>
              multiple
              disableCloseOnSelect
              limitTags={3}
              options={linkedServicesOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              value={linkedServicesValue}
              onChange={(_, newValue) => {
                setForm((prev) => ({
                  ...prev,
                  serviceIds: newValue.map((s) => String(s.id)),
                }));
                setResourceFieldErrors((prev) => (prev.serviceIds ? { ...prev, serviceIds: undefined } : prev));
              }}
              ListboxProps={{ sx: { maxHeight: 320 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t("adminResources.linkedServices")}
                  error={Boolean(resourceFieldErrors.serviceIds)}
                  helperText={
                    resourceFieldErrors.serviceIds ? resourceFieldErrors.serviceIds : t("adminResources.linkedServicesHint")
                  }
                  data-testid="select-resource-linked-services"
                />
              )}
            />
          )}
          <FormControlLabel
            control={<Switch checked={form.active} onChange={(_, v) => setForm({ ...form, active: v })} data-testid="switch-resource-active" />}
            label={t("adminResources.active")}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isPending} data-testid="button-save-resource">
            {editing ? t("adminResources.saveChanges") : t("adminResources.createResource")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t("adminResources.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("adminResources.confirmDeleteBody", { name: deleteTarget?.name ?? "" })}
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setDeleteTarget(null)}>
            {t("common.cancel")}
          </Button>
          <Button color="error" variant="contained" onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
